import { VisionEvent } from '../../api/client';

const STALE_MS = 10 * 60 * 1000;

function capturedLabel(capturedAt: string): string {
  const d = new Date(capturedAt);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
}

function isStale(capturedAt: string | undefined): boolean {
  if (!capturedAt) return true;
  return Date.now() - new Date(capturedAt).getTime() > STALE_MS;
}

const ROAST_LEVELS = [
  { label: 'Coffee 1',  color: '#748060', score: 5  },
  { label: 'Coffee 2',  color: '#917D60', score: 15 },
  { label: 'Coffee 3',  color: '#846A52', score: 25 },
  { label: 'Coffee 4',  color: '#745642', score: 35 },
  { label: 'Coffee 5',  color: '#5E4231', score: 45 },
  { label: 'Coffee 6',  color: '#483227', score: 55 },
  { label: 'Coffee 7',  color: '#332621', score: 65 },
  { label: 'Coffee 8',  color: '#221D1B', score: 75 },
  { label: 'Coffee 9',  color: '#1B1817', score: 85 },
  { label: 'Coffee 10', color: '#141414', score: 95 },
];

interface Props {
  visionEvent: VisionEvent | null;
  targetRoastScore?: number | null;
}

export default function ColorHistogram({ visionEvent, targetRoastScore }: Props) {
  const histogram = visionEvent?.roast_level_histogram;
  const stale = isStale(visionEvent?.captured_at);

  if (!histogram || histogram.length === 0 || stale) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-1 text-center">
        <span className="text-gray-300 text-2xl">📷</span>
        <span className="text-gray-400 text-sm">ממתין לנתוני צבע...</span>
        {visionEvent?.captured_at && stale && (
          <span className="text-xs text-gray-300">עודכן לאחרונה: {capturedLabel(visionEvent.captured_at)}</span>
        )}
      </div>
    );
  }

  const maxVal = Math.max(...histogram, 0.01);
  const targetPct = targetRoastScore != null ? (targetRoastScore / 100) * 100 : null;

  return (
    <div className="flex flex-col h-full gap-1 px-1" dir="ltr">
      <div className="flex items-end gap-1 flex-1 min-h-0 relative">
        {/* Target score vertical line */}
        {targetPct != null && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-amber-500 z-10 opacity-80"
            style={{ left: `${targetPct}%` }}
            title={`יעד: ${Math.round(targetRoastScore!)} נק׳`}
          >
            <div className="absolute -top-1 -translate-x-1/2 w-2 h-2 bg-amber-500 rounded-full" />
          </div>
        )}

        {histogram.map((pct, i) => {
          const level = ROAST_LEVELS[i];
          const heightPct = maxVal > 0 ? (pct / maxVal) * 100 : 0;
          return (
            <div key={i} className="flex flex-col items-center gap-0.5 flex-1" title={`${level.label}: ${(pct * 100).toFixed(1)}%`}>
              <div className="w-full flex-1 flex items-end">
                <div
                  className="w-full rounded-t transition-all duration-500"
                  style={{ height: `${heightPct}%`, backgroundColor: level.color, minHeight: pct > 0 ? 2 : 0 }}
                />
              </div>
              <div className="text-[8px] text-gray-500 writing-mode-vertical" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', lineHeight: 1 }}>
                {level.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Current level label + timestamp */}
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-amber-700">
          {visionEvent?.roast_level_label ?? ''}
          {visionEvent?.roast_level_score != null && ` (${Math.round(visionEvent.roast_level_score)})`}
        </span>
        {visionEvent?.captured_at && (
          <span className="text-gray-400">{capturedLabel(visionEvent.captured_at)}</span>
        )}
      </div>
    </div>
  );
}
